import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Search, Users, Store, Package, IndianRupee } from "lucide-react";
import api, { inr, formatError } from "@/lib/api";
import { StatusBadge } from "@/pages/OrdersPage";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

function Kpi({ label, value, testId }) {
  return (
    <div className="border-b sm:border-b-0 border-r border-slate-100 last:border-0 px-5 py-4">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p data-testid={testId} className="font-heading text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId} className="border border-slate-100 rounded-xl overflow-hidden mt-6">
      <p className="px-5 py-3 bg-slate-50 text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </p>
      {children}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  if (!data) return <div className="h-96 bg-slate-100 rounded-2xl animate-pulse mt-6" />;

  const k = data.kpis;

  return (
    <div data-testid="analytics-section">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 border border-slate-100 rounded-xl overflow-hidden bg-slate-50/50">
        <Kpi label="Users" value={k.users_total} testId="kpi-users" />
        <Kpi label="Customers" value={k.customers} testId="kpi-customers" />
        <Kpi label="Vendors" value={k.vendors} testId="kpi-vendors" />
        <Kpi label="Products" value={k.products} testId="kpi-products" />
        <Kpi label="Orders" value={k.orders} testId="kpi-orders" />
        <Kpi label="GMV" value={inr(k.gmv)} testId="kpi-gmv" />
        <Kpi label="Platform fees" value={inr(k.platform_fees)} testId="kpi-fees" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 border border-slate-100 rounded-xl overflow-hidden bg-slate-50/50 mt-3">
        <Kpi label="New users (7d)" value={k.new_users_7d} testId="kpi-new-users" />
        <Kpi label="Repeat buyers" value={k.repeat_buyers} testId="kpi-repeat-buyers" />
        <Kpi label="Product views" value={k.product_views} testId="kpi-views" />
        <Kpi label="Searches" value={k.searches} testId="kpi-searches" />
        <Kpi label="Reviews" value={k.reviews} testId="kpi-reviews" />
        <Kpi label="Refunds" value={inr(k.refunds)} testId="kpi-refunds" />
        <Kpi label="Payouts pending" value={inr(data.settlements.pending)} testId="kpi-payouts" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="border border-slate-100 rounded-xl p-5">
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">GMV by day</p>
          {data.gmv_series.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={data.gmv_series}>
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 9 }} />
                <Tooltip formatter={(v) => inr(v)} />
                <Line type="monotone" dataKey="gmv" stroke="#0f172a" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-10 text-center">No orders yet.</p>}
        </div>
        <div className="border border-slate-100 rounded-xl p-5">
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Signups by day</p>
          {data.signup_series.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={data.signup_series}>
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="signups" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-10 text-center">No signups yet.</p>}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mt-6" data-testid="orders-by-status">
        <span className="text-xs uppercase tracking-widest text-slate-400 font-bold py-1.5">Order statuses:</span>
        {Object.entries(data.orders_by_status).map(([s, n]) => (
          <span key={s} className="inline-flex items-center gap-1.5"><StatusBadge status={s} /><span className="text-xs font-bold">{n}</span></span>
        ))}
        {!Object.keys(data.orders_by_status).length && <span className="text-xs text-slate-400 py-1.5">No orders yet</span>}
        <span className="text-xs uppercase tracking-widest text-slate-400 font-bold py-1.5 ml-4">Payments:</span>
        {Object.entries(data.payment_methods).map(([m, n]) => (
          <span key={m} className="text-xs bg-slate-100 px-2.5 py-1 rounded-full font-semibold">{m.replace(/_/g, " ")}: {n}</span>
        ))}
        <span className="text-xs uppercase tracking-widest text-slate-400 font-bold py-1.5 ml-4">Returns:</span>
        <span className="text-xs bg-slate-100 px-2.5 py-1 rounded-full font-semibold">{data.returns.total} total · {data.returns.pending} pending</span>
      </div>

      <Section title="All users — activity & spend" icon={Users} testId="analytics-users">
        <Table>
          <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Joined</TableHead><TableHead>Orders</TableHead><TableHead>Total spent</TableHead><TableHead>Reviews</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.users.map((u) => (
              <TableRow key={u.id} data-testid={`analytics-user-${u.id}`}>
                <TableCell><p className="font-semibold text-sm">{u.name}</p><p className="text-[11px] text-slate-400">{u.email}</p></TableCell>
                <TableCell><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-slate-900 text-white" : u.role === "seller" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>{u.role}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString("en-IN")}</TableCell>
                <TableCell className="font-semibold">{u.orders}</TableCell>
                <TableCell className="font-bold">{inr(u.spent)}</TableCell>
                <TableCell>{u.reviews}</TableCell>
              </TableRow>
            ))}
            {!data.users.length && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400 text-sm">No users yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Section>

      <Section title="All vendors — performance" icon={Store} testId="analytics-vendors">
        <Table>
          <TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead>Status</TableHead><TableHead>Fee</TableHead><TableHead>Listings</TableHead><TableHead>Stock</TableHead><TableHead>Orders</TableHead><TableHead>Units</TableHead><TableHead>GMV</TableHead><TableHead>Cancel %</TableHead><TableHead>Return %</TableHead><TableHead>Rating</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.vendors.map((v) => (
              <TableRow key={v.id} data-testid={`analytics-vendor-${v.id}`}>
                <TableCell className="font-semibold text-sm">{v.business_name}</TableCell>
                <TableCell><StatusBadge status={v.status} /></TableCell>
                <TableCell className="text-xs">{v.platform_fee != null ? inr(v.platform_fee) : "—"}{v.fee_paid && <span className="block text-emerald-600 font-bold">Paid</span>}</TableCell>
                <TableCell>{v.listings}</TableCell>
                <TableCell>{v.stock}</TableCell>
                <TableCell>{v.orders}</TableCell>
                <TableCell>{v.units}</TableCell>
                <TableCell className="font-bold">{inr(v.gmv)}</TableCell>
                <TableCell className={v.cancel_rate > 20 ? "text-red-600 font-bold" : ""}>{v.cancel_rate}%</TableCell>
                <TableCell className={v.return_rate > 30 ? "text-red-600 font-bold" : ""}>{v.return_rate}%</TableCell>
                <TableCell>{v.rating}</TableCell>
              </TableRow>
            ))}
            {!data.vendors.length && <TableRow><TableCell colSpan={11} className="text-center py-10 text-slate-400 text-sm">No vendors yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Section>

      <Section title="All products — sales & engagement" icon={Package} testId="analytics-products">
        <Table>
          <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Category</TableHead><TableHead>Offers</TableHead><TableHead>Stock</TableHead><TableHead>Views</TableHead><TableHead>Units sold</TableHead><TableHead>Revenue</TableHead><TableHead>Rating</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.products.map((p) => (
              <TableRow key={p.id} data-testid={`analytics-product-${p.id}`}>
                <TableCell><p className="font-semibold text-sm line-clamp-1 max-w-[240px]">{p.title}</p><p className="text-[11px] text-slate-400">{p.brand}</p></TableCell>
                <TableCell className="text-xs">{p.category}</TableCell>
                <TableCell>{p.offers}</TableCell>
                <TableCell className={p.stock < 5 ? "text-red-600 font-bold" : ""}>{p.stock}</TableCell>
                <TableCell>{p.views}</TableCell>
                <TableCell>{p.units_sold}</TableCell>
                <TableCell className="font-bold">{inr(p.revenue)}</TableCell>
                <TableCell>{p.rating} ({p.review_count})</TableCell>
              </TableRow>
            ))}
            {!data.products.length && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400 text-sm">No products yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Section>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Top search queries" icon={Search} testId="analytics-searches">
          <div className="p-5 flex gap-2 flex-wrap">
            {data.top_searches.map((s) => (
              <span key={s.query} data-testid={`top-search-${s.query.replace(/[^a-z0-9]+/g, "-")}`} className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full font-semibold">
                {s.query} · {s.count}
              </span>
            ))}
            {!data.top_searches.length && <p className="text-sm text-slate-400">No searches tracked yet.</p>}
          </div>
        </Section>
        <Section title="Recent user activity" icon={Eye} testId="analytics-activity">
          <div className="p-5 space-y-2 max-h-72 overflow-y-auto">
            {data.recent_activity.map((a) => (
              <p key={a.id} className="text-xs text-slate-600 flex justify-between gap-4">
                <span>{a.type === "search" ? `Searched "${a.query}"` : "Viewed a product"}{a.user_id ? " (signed in)" : " (guest)"}</span>
                <span className="text-slate-400 shrink-0">{new Date(a.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </p>
            ))}
            {!data.recent_activity.length && <p className="text-sm text-slate-400">No activity tracked yet.</p>}
          </div>
        </Section>
      </div>
    </div>
  );
}
